// Executive Dashboard · /diretoria
// Reutiliza os MESMOS endpoints do Dashboard principal:
//   GET /headcount/summary  ·  /headcount/timeline  ·  /headcount/evolution
//   GET /dashboard/alerts   ·  /dashboard/realized  ·  /dashboard/goals
// Nenhuma nova regra de negócio ou fórmula.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Users, UsersRound, Briefcase, TrendingUp, TrendingDown,
  Gauge, Activity, ShieldAlert, Grid3x3, Target, Crown,
  Download, FileText, FileImage, Minus, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { exportDashboardPng, exportDashboardPdf } from "@/lib/dashboardSnapshot";

const nfmt = (n) => (n ?? 0).toLocaleString("pt-BR");
const pctFmt = (n, d = 2) => (n ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d }) + "%";

function prevPeriodStr() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ExecutiveDashboardPage() {
  const { companyParam, selectedCompany } = useCompany();
  const [summary, setSummary] = useState(null);
  const [prev, setPrev] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [realized, setRealized] = useState(null);
  const [goals, setGoals] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [evoMonths, setEvoMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = companyParam ? { company_id: companyParam } : {};
    const prevParams = { ...params, period: prevPeriodStr() };
    Promise.all([
      api.get("/headcount/summary", { params }),
      api.get("/headcount/summary", { params: prevParams }).catch(() => ({ data: null })),
      api.get("/dashboard/alerts",  { params }).catch(() => ({ data: null })),
      api.get("/dashboard/realized",{ params }).catch(() => ({ data: null })),
      api.get("/dashboard/goals",   { params }).catch(() => ({ data: { goals: [] } })),
      api.get("/headcount/timeline",{ params: { ...params, months: 12 } }).catch(() => ({ data: null })),
    ]).then(([s, p, a, r, g, tl]) => {
      if (cancel) return;
      setSummary(s.data); setPrev(p.data); setAlerts(a.data);
      setRealized(r.data); setGoals(g.data?.goals || []); setTimeline(tl.data);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [companyParam]);

  useEffect(() => {
    let cancel = false;
    const params = companyParam ? { company_id: companyParam } : {};
    api.get("/headcount/evolution", { params: { ...params, months: evoMonths } })
      .then(({ data }) => { if (!cancel) setEvolution(data); })
      .catch(() => { if (!cancel) setEvolution(null); });
    return () => { cancel = true; };
  }, [companyParam, evoMonths]);

  // ------------- derived (mesma fórmula do Dashboard principal) -------------
  const byKey = useMemo(() => {
    const m = {};
    (summary?.categorias_vinculo || []).forEach((c) => { m[c.key] = c; });
    return m;
  }, [summary]);
  const totCLT     = byKey.CLT?.total || 0;
  const totAprend  = byKey.Aprendiz?.total || 0;
  const totTempor  = byKey.Temporario?.total || 0;
  const totPJ      = byKey.PJ?.total || 0;
  const totAutonom = byKey.Autonomo?.total || 0;
  const totalColab = totCLT + totAprend + totTempor;
  const totalMO    = totalColab + totPJ + totAutonom;

  const prevByKey = useMemo(() => {
    const m = {};
    (prev?.categorias_vinculo || []).forEach((c) => { m[c.key] = c; });
    return m;
  }, [prev]);
  const prevTotalColab = (prevByKey.CLT?.total || 0) + (prevByKey.Aprendiz?.total || 0) + (prevByKey.Temporario?.total || 0);
  const prevTotalMO    = prevTotalColab + (prevByKey.PJ?.total || 0) + (prevByKey.Autonomo?.total || 0);

  const adm = summary?.movimentacao_mes?.admitidos ?? 0;
  const dem = summary?.movimentacao_mes?.demitidos ?? 0;
  const saldo = adm - dem;

  const turnover = alerts?.turnover;
  const rz = realized?.realized || {};

  // Meta ativa mais específica por indicador
  const goalsByInd = useMemo(() => {
    const m = {};
    (goals || []).forEach((g) => {
      if (!g.active) return;
      const rank = (x) => x.scope === "setor" ? 3 : x.scope === "empresa" ? 2 : 1;
      if (!m[g.indicator] || rank(g) > rank(m[g.indicator])) m[g.indicator] = g;
    });
    return m;
  }, [goals]);

  const doExport = async (kind) => {
    setExporting(true);
    try {
      const label = selectedCompany?.nome_fantasia || selectedCompany?.razao_social || "Grupo ALCON";
      const period = `${summary?.period?.start || ""} → ${summary?.period?.end_exclusive || ""}`;
      if (kind === "png") await exportDashboardPng({ companyLabel: label, nodeId: "exec-snapshot" });
      else await exportDashboardPdf({ companyLabel: label, periodLabel: period, nodeId: "exec-snapshot" });
      toast.success(`Snapshot ${kind.toUpperCase()} gerado.`);
    } catch (e) { toast.error(`Falha: ${e.message || e}`); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-3 pb-6" data-testid="executive-dashboard">
      {/* Header (fora do snapshot) */}
      <header className="flex items-center justify-between flex-wrap gap-2" data-snapshot-hide>
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-[#C99328]" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">Painel da Diretoria</div>
            <h1 className="font-display text-[20px] font-semibold text-slate-900 leading-tight">
              {selectedCompany?.nome_fantasia || "Grupo ALCON · Consolidado"}
            </h1>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" disabled={loading || exporting}
                    className="h-8 border-slate-300" data-testid="exec-export">
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {exporting ? "Exportando..." : "Snapshot"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("pdf")}><FileText className="h-4 w-4 mr-2 text-rose-600" />PDF (A4 paisagem)</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("png")}><FileImage className="h-4 w-4 mr-2 text-[#065F46]" />PNG (2x)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Snapshot container */}
      <div id="exec-snapshot" className="space-y-3">

      {/* Faixa de 4 KPIs estratégicos */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <ExecKpi
          testId="exec-kpi-colab"
          label="Colaboradores"
          value={loading ? null : nfmt(totalColab)}
          hint={`CLT ${totCLT} · Apr ${totAprend} · Temp ${totTempor}`}
          trend={<Trend cur={totalColab} prev={prevTotalColab} />}
          icon={Users}
        />
        <ExecKpi
          testId="exec-kpi-mao-de-obra"
          label="Mão de Obra"
          value={loading ? null : nfmt(totalMO)}
          hint={`Colab ${totalColab} + PJ ${totPJ}${totAutonom?` + Aut ${totAutonom}`:""}`}
          trend={<Trend cur={totalMO} prev={prevTotalMO} light />}
          icon={UsersRound}
          hero
        />
        <ExecKpi
          testId="exec-kpi-turnover"
          label={`Turnover · ${turnover?.period || "mês"}`}
          value={loading ? null : (turnover ? pctFmt(turnover.pct) : "—")}
          hint={turnover ? `Limite ${turnover.limit}%${turnover.status==="critico"?" · acima":""}` : ""}
          tone={turnover?.status === "critico" ? "bad" : "ok"}
          icon={Gauge}
        />
        <ExecKpi
          testId="exec-kpi-saldo"
          label={`Saldo · ${summary?.movimentacao_mes?.periodo_iso?.mes || "mês"}`}
          value={loading ? null : (saldo > 0 ? `+${saldo}` : `${saldo}`)}
          hint={`${adm} admissões · ${dem} demissões`}
          tone={saldo > 0 ? "ok" : saldo < 0 ? "bad" : "muted"}
          icon={Activity}
        />
      </div>

      {/* Metas × Realizado (grid horizontal 4 colunas) */}
      <section className="rounded-xl border border-slate-200 bg-white p-3" data-testid="exec-goals">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5 text-[#065F46]" />
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-700">Metas × Realizado</h2>
          </div>
          <span className="text-[10.5px] text-slate-400">{goals.length ? `${goals.length} meta(s) ativas` : "sem metas cadastradas"}</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {["turnover","absenteismo","headcount","horas_extras"].map((ind) => (
            <GoalTile key={ind} ind={ind} goal={goalsByInd[ind]} real={rz[ind]} />
          ))}
        </div>
      </section>

      {/* Alertas · semáforo (mesma origem: /dashboard/alerts) */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-2.5" data-testid="exec-alerts">
        <MiniAlert testId="exec-alert-critico" tone="bad" icon={ShieldAlert}
          label="Setores acima do quadro" value={alerts?.summary?.critico ?? 0} />
        <MiniAlert testId="exec-alert-atencao" tone="warn" icon={Activity}
          label={`Em atenção (≥${Math.round((alerts?.params?.warn_ratio||0.9)*100)}%)`}
          value={alerts?.summary?.atencao ?? 0} />
        <MiniAlert testId="exec-alert-normal" tone="ok" icon={Users}
          label="Dentro do quadro" value={alerts?.summary?.normal ?? 0} />
        <MiniAlert testId="exec-alert-sem-quadro" tone="muted" icon={Minus}
          label="Sem quadro definido" value={alerts?.summary?.sem_quadro ?? 0} />
      </section>

      {/* Gráficos lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Panel title="Evolução · admissões vs. demissões" testId="exec-evo"
               right={
                 <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                   {[3,6,12].map((m) => (
                     <button key={m} onClick={() => setEvoMonths(m)}
                             data-testid={`exec-evo-${m}m`}
                             className={`px-2 py-0.5 text-[10.5px] font-semibold ${evoMonths===m?"bg-[#065F46] text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>{m}M</button>
                   ))}
                 </div>
               }>
          {loading || !evolution ? <Skeleton className="h-[190px]" /> :
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={(evolution.months||[]).map((m,i)=>({month:m,Admitidos:evolution.series?.[0]?.data?.[i]??0,Demitidos:evolution.series?.[1]?.data?.[i]??0}))}
                        margin={{top:8,right:8,left:-16,bottom:0}} barCategoryGap="18%">
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{fontSize:10,fill:"#64748B"}} axisLine={{stroke:"#E5E7EB"}} tickLine={false} />
                <YAxis tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{borderRadius:8,fontSize:12,border:"1px solid #E5E7EB"}}
                  labelFormatter={(l,pl)=>{const s=(pl?.[0]?.payload?.Admitidos||0)-(pl?.[0]?.payload?.Demitidos||0);return `${l} · Saldo ${s>0?"+":""}${s}`;}} />
                <Legend iconType="circle" iconSize={7} wrapperStyle={{fontSize:10,paddingTop:2}} />
                <Bar dataKey="Admitidos" fill="#065F46" radius={[3,3,0,0]} />
                <Bar dataKey="Demitidos" fill="#EF4444" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>}
        </Panel>

        <Panel title="Mão de obra · CLT / PJ / Total (12M)" testId="exec-timeline">
          {loading || !timeline ? <Skeleton className="h-[190px]" /> :
            <ResponsiveContainer width="100%" height={190}>
              <LineChart data={(timeline.months||[]).map((m,i)=>({month:m,CLT:timeline.series?.clt?.[i]??0,PJ:timeline.series?.pj?.[i]??0,Total:timeline.series?.total?.[i]??0}))}
                         margin={{top:8,right:8,left:-16,bottom:0}}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="month" tick={{fontSize:10,fill:"#64748B"}} axisLine={{stroke:"#E5E7EB"}} tickLine={false} />
                <YAxis tick={{fontSize:10,fill:"#64748B"}} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{borderRadius:8,fontSize:12,border:"1px solid #E5E7EB"}} />
                <Legend iconType="circle" iconSize={7} wrapperStyle={{fontSize:10,paddingTop:2}} />
                <Line type="monotone" dataKey="CLT" stroke="#065F46" strokeWidth={2.2} dot={{r:2}} />
                <Line type="monotone" dataKey="PJ"  stroke="#C99328" strokeWidth={2.2} dot={{r:2}} />
                <Line type="monotone" dataKey="Total" stroke="#0F172A" strokeWidth={2.2} strokeDasharray="4 3" dot={{r:2}} />
              </LineChart>
            </ResponsiveContainer>}
        </Panel>
      </div>

      {/* Top setores críticos */}
      <Panel title="Top setores acima ou próximos do quadro" testId="exec-heatmap"
             right={alerts ? `${(alerts.sectors||[]).length} setor(es)` : ""}>
        {loading || !alerts ? <Skeleton className="h-[150px]" /> :
          <div className="max-h-[220px] overflow-y-auto">
            {(alerts.sectors || [])
              .filter((s) => s.status === "critico" || s.status === "atencao")
              .slice(0, 12)
              .map((s) => {
                const bg = s.status === "critico"
                  ? (s.diferenca / Math.max(1, s.quadro_aprovado) > 0.1 ? "#DC2626" : "#EF4444")
                  : "#FDE68A";
                const fg = s.status === "critico" ? "#FFF" : "#92400E";
                return (
                  <div key={s.sector_id}
                       className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 py-1 px-1.5 rounded hover:bg-slate-50 text-[11.5px]">
                    <span className="text-slate-700 truncate">{s.name}</span>
                    <span className="text-slate-500 tabular-nums w-16 text-right">{s.atual_clt}/{s.quadro_aprovado}</span>
                    <span className="tabular-nums rounded px-1.5 py-0.5 min-w-[40px] text-center font-semibold text-[11px]" style={{background:bg,color:fg}}>
                      {s.diferenca > 0 ? `+${s.diferenca}` : s.diferenca}
                    </span>
                    <span className="text-slate-500 tabular-nums w-11 text-right">{s.ocupacao_pct !== null ? `${s.ocupacao_pct}%` : "—"}</span>
                  </div>
                );
              })}
            {(alerts.sectors||[]).filter(s=>s.status!=="critico"&&s.status!=="atencao").length === (alerts.sectors||[]).length && (
              <div className="text-center text-[11px] text-emerald-700 py-4">✓ Nenhum setor em situação crítica.</div>
            )}
          </div>}
      </Panel>

      </div>{/* /exec-snapshot */}
    </div>
  );
}

// ---- Auxiliares ---------------------------------------------------------
function ExecKpi({ label, value, hint, icon: Icon, trend, tone, hero, testId }) {
  const tones = {
    ok:    { wrap:"bg-white border-emerald-200",     val:"text-emerald-700", ico:"bg-emerald-100 text-emerald-600" },
    bad:   { wrap:"bg-white border-rose-200",        val:"text-rose-700",    ico:"bg-rose-100 text-rose-600" },
    warn:  { wrap:"bg-white border-amber-200",       val:"text-amber-700",   ico:"bg-amber-100 text-amber-700" },
    muted: { wrap:"bg-white border-slate-200",       val:"text-slate-500",   ico:"bg-slate-100 text-slate-600" },
    def:   { wrap:"bg-white border-slate-200",       val:"text-slate-900",   ico:"bg-slate-100 text-slate-700" },
    hero:  { wrap:"bg-[#064E3B] border-transparent text-white", val:"text-white", ico:"bg-white/15 text-emerald-100" },
  };
  const s = hero ? tones.hero : (tones[tone] || tones.def);
  return (
    <div className={`rounded-xl border px-3.5 py-2.5 min-h-[104px] ${s.wrap}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${hero?"text-emerald-100/90":"text-slate-500"}`}>{label}</div>
        <span className={`h-6 w-6 rounded-md inline-flex items-center justify-center ${s.ico}`}><Icon className="h-3 w-3" /></span>
      </div>
      <div className={`mt-1.5 font-display text-[28px] font-bold tabular-nums leading-none ${s.val}`}>{value ?? "—"}</div>
      {trend && <div className="mt-1">{trend}</div>}
      {hint && <div className={`mt-1 text-[10.5px] leading-tight ${hero?"text-emerald-100/80":"text-slate-500"}`}>{hint}</div>}
    </div>
  );
}

function Trend({ cur, prev, light }) {
  if (prev === null || prev === undefined || prev === 0) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  if (!isFinite(pct)) return null;
  const up = pct > 0.05, down = pct < -0.05;
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const cls = light
    ? (up ? "text-emerald-200" : down ? "text-rose-200" : "text-emerald-100/80")
    : (up ? "text-emerald-600" : down ? "text-rose-600" : "text-slate-500");
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-medium ${cls}`}>
      <Icon className="h-3 w-3" />{Math.abs(pct).toFixed(1)}% vs. mês ant.
    </span>
  );
}

function MiniAlert({ label, value, icon: Icon, tone, testId }) {
  const tones = {
    bad:   { wrap:"bg-white border-rose-200",     val:"text-rose-700",    ico:"bg-rose-100 text-rose-600" },
    warn:  { wrap:"bg-white border-amber-200",    val:"text-amber-700",   ico:"bg-amber-100 text-amber-700" },
    ok:    { wrap:"bg-white border-emerald-200", val:"text-emerald-700", ico:"bg-emerald-100 text-emerald-600" },
    muted: { wrap:"bg-white border-slate-200",   val:"text-slate-500",   ico:"bg-slate-100 text-slate-500" },
  };
  const s = tones[tone] || tones.muted;
  return (
    <div className={`rounded-lg border px-3 py-2 min-h-[64px] ${s.wrap}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className={`h-5 w-5 rounded-md inline-flex items-center justify-center ${s.ico}`}><Icon className="h-3 w-3" /></span>
        <div className={`font-display text-[22px] font-bold tabular-nums leading-none ${s.val}`}>{nfmt(value)}</div>
      </div>
      <div className="mt-1 text-[10.5px] leading-tight text-slate-600">{label}</div>
    </div>
  );
}

function Panel({ title, right, testId, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-700">{title}</h2>
        {right && <div className="text-[10.5px] text-slate-500">{right}</div>}
      </div>
      {children}
    </div>
  );
}

const IND_LABEL = { turnover:"Turnover", absenteismo:"Absenteísmo", headcount:"Headcount", horas_extras:"Horas Extras" };

function GoalTile({ ind, goal, real }) {
  const value = real?.value;
  const unit  = goal?.unit || real?.unit || (ind === "headcount" ? "qtd" : ind === "horas_extras" ? "h" : "%");
  const hasT = goal?.target !== undefined && goal?.target !== null;
  const hasR = value !== undefined && value !== null;
  let status = "info", delta = null;
  if (hasT && hasR) {
    delta = value - goal.target;
    const bad = goal.operator === "<=" ? delta > 0 : goal.operator === ">=" ? delta < 0 : Math.abs(delta) > 0.05;
    status = bad ? "critico" : "normal";
  }
  const dot = status === "critico" ? "bg-rose-500" : status === "normal" ? "bg-emerald-500" : "bg-slate-300";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 flex flex-col gap-0.5" data-testid={`exec-goal-${ind}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600">{IND_LABEL[ind]}</div>
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      </div>
      <div className="text-[16px] font-display font-bold tabular-nums text-slate-900 leading-none mt-0.5">
        {hasR ? (unit === "%" ? pctFmt(value) : nfmt(value)) : "—"}
      </div>
      <div className="text-[10px] text-slate-500 leading-tight">
        {hasT ? (
          <>Meta {goal.operator} {goal.target}{unit === "%" ? "%" : ` ${unit}`}
            {" · "}
            <span className={status === "critico" ? "text-rose-700 font-semibold" : status === "normal" ? "text-emerald-700 font-semibold" : ""}>
              {delta !== null ? `${delta > 0 ? "+" : ""}${Number(delta).toFixed(unit === "%" ? 2 : 0)}${unit === "%" ? " p.p." : ""}` : ""}
            </span>
          </>
        ) : (
          <span className="italic text-slate-400">meta não definida</span>
        )}
      </div>
    </div>
  );
}
